import React, { Component } from "react";
import "../../App.css";
import PyramidDiagram from "./pyramidDiagram";
import pyramidExample from "./pyramid-example.png";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";

const FIELDS = [
    { key: "niftyStart", label: "Start Price" },
    { key: "percentFall", label: "Percent fall per drop (%)" },
    { key: "x", label: "X — starting investment (₹)" },
    { key: "y", label: "Y — increment investment (₹)" },
    { key: "drops", label: "Number of purchases" },
];

const DEFAULT_INPUTS = { niftyStart: "25000", percentFall: "1", x: "500", y: "500", drops: "10" };

// Buys at niftyStart, then again each time the price falls another `percentFall`% from the previous
// buy level (compounding), investing x on the first buy and y more on each next one, then reports the
// resulting average buy price. Stops early if the price would reach zero.
const calculateAveraging = ({ niftyStart, percentFall, x, y, drops }) => {
    const fallFactor = (100 - percentFall) / 100;
    const purchases = [];
    let totalInvestment = 0;
    let totalUnits = 0;

    for (let i = 0; i < drops; i++) {
        const price = niftyStart * Math.pow(fallFactor, i);
        if (price <= 0) break;
        const investment = x + i * y;
        const units = investment / price;

        totalInvestment += investment;
        totalUnits += units;

        purchases.push({ dropNumber: i + 1, price, investment, units });
    }

    const averagePrice = totalUnits > 0 ? totalInvestment / totalUnits : 0;
    // Price of the last purchase — how far the average sits above where buying stopped.
    const niftyEndLevel = purchases.length ? purchases[purchases.length - 1].price : 0;
    const awayPercent = niftyEndLevel > 0 ? ((averagePrice - niftyEndLevel) / niftyEndLevel) * 100 : 0;

    // How much of a fall the plan covers, from the first buy down to the last.
    const totalFallPoints = purchases.length ? niftyStart - niftyEndLevel : 0;
    const totalFallPercent = niftyStart > 0 ? (totalFallPoints / niftyStart) * 100 : 0;

    return {
        purchases,
        totalInvestment,
        totalUnits,
        averagePrice,
        niftyEndLevel,
        awayPercent,
        totalFallPoints,
        totalFallPercent,
    };
};

const toNumber = value => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value, fractionDigits = 0) =>
    value.toLocaleString("en-IN", { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });

class Pyramid extends Component {
    state = { inputs: DEFAULT_INPUTS };

    handleChange = (key, value) => {
        this.setState(prev => ({ inputs: { ...prev.inputs, [key]: value } }));
    };

    render() {
        const { inputs } = this.state;
        const result = calculateAveraging({
            niftyStart: toNumber(inputs.niftyStart),
            percentFall: toNumber(inputs.percentFall),
            x: toNumber(inputs.x),
            y: toNumber(inputs.y),
            drops: Math.max(0, Math.floor(toNumber(inputs.drops))),
        });

        return (
            <div>
                <header>
                    <h1 className="pyramid-heading">
                        Pyramid Calculator
                        <span className="h1-subtext">
                            <Popup
                                modal
                                closeOnDocumentClick
                                className="pyramid-example"
                                trigger={
                                    <button type="button" className="pyramid-example-link">
                                        Real Time Example
                                    </button>
                                }
                            >
                                {close => (
                                    <div className="pyramid-example-body">
                                        <button
                                            type="button"
                                            className="pyramid-example-close"
                                            aria-label="Close example"
                                            onClick={close}
                                        >
                                            ×
                                        </button>
                                        <img
                                            src={pyramidExample}
                                            alt="NIFTY chart showing SIP levels on each drop, with investments growing from X to X+10Y"
                                        />
                                    </div>
                                )}
                            </Popup>
                        </span>
                    </h1>
                </header>
                <div className="pyramid-layout">
                    <div className="pyramid-card">
                        <div className="pyramid-inputs">
                            {FIELDS.map(field => (
                                <label className="pyramid-field" key={field.key}>
                                    <span>{field.label}</span>
                                    <input
                                        type="number"
                                        value={inputs[field.key]}
                                        onChange={e => this.handleChange(field.key, e.target.value)}
                                    />
                                </label>
                            ))}
                        </div>

                        <div className="pyramid-summary">
                            <div className="pyramid-totals">
                                <div>
                                    <span className="pyramid-summary-label">Average buying price</span>
                                    <span className="pyramid-average">{formatNumber(result.averagePrice, 2)}</span>
                                </div>
                                <div>
                                    <span className="pyramid-summary-label">
                                        Away from end level ({formatNumber(result.niftyEndLevel, 2)})
                                    </span>
                                    <span className="pyramid-average">{result.awayPercent.toFixed(2)}%</span>
                                    <span className="pyramid-summary-note">
                                        Total fall entertained: {result.totalFallPercent.toFixed(2)}% (
                                        {formatNumber(result.totalFallPoints, 2)} pts)
                                    </span>
                                </div>
                            </div>
                            <div className="pyramid-totals">
                                <div>
                                    <span className="pyramid-summary-label">Total investment</span>
                                    <span className="pyramid-total-value">₹{formatNumber(result.totalInvestment)}</span>
                                </div>
                                <div>
                                    <span className="pyramid-summary-label">Total units</span>
                                    <span className="pyramid-total-value">{result.totalUnits.toFixed(6)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pyramid-breakdown">
                            <h4>Purchase breakdown</h4>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Drop</th>
                                        <th>Price</th>
                                        <th>Invested</th>
                                        <th>Units</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.purchases.map(purchase => (
                                        <tr key={purchase.dropNumber}>
                                            <td>{purchase.dropNumber}</td>
                                            <td>{formatNumber(purchase.price, 2)}</td>
                                            <td>₹{formatNumber(purchase.investment)}</td>
                                            <td>{purchase.units.toFixed(6)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <PyramidDiagram
                        purchases={result.purchases}
                        averagePrice={result.averagePrice}
                        awayPercent={result.awayPercent}
                        totalFallPercent={result.totalFallPercent}
                        totalFallPoints={result.totalFallPoints}
                    />
                </div>
            </div>
        );
    }
}

export default Pyramid;
